import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { allBooks } from '../data/booksData'
import {
  spiceLevels,
  darknessLevels,
  povTypes,
  tenseTypes,
} from '../data/bookTags'

export const useStoryStack = () => {
  const [searchParams] = useSearchParams()
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [moodInput, setMoodInput] = useState('')
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (!initialized) {
      const tagsParam = searchParams.get('tags')
      if (tagsParam) {
        const tags = tagsParam.split(',').map((t) => decodeURIComponent(t))
        setSelectedTags(tags)
      }
      setInitialized(true)
    }
  }, [searchParams, initialized])

  const addTag = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]))
  }

  const removeTag = (tag: string) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag))
  }

  const clearAll = () => {
    setSelectedTags([])
    setMoodInput('')
  }

  const filteredBooks = useMemo(() => {
    if (selectedTags.length === 0) return allBooks

    return allBooks.filter((book) => {
      return selectedTags.every((tag) => {
        const tagLower = tag.toLowerCase()
        const tagId = tagLower.replace(/\s+/g, '-')

        // Check tags array
        if (
          book.tags?.some(
            (t) => t.toLowerCase() === tagLower || t.toLowerCase() === tagId,
          )
        )
          return true

        // Check genres
        if (book.genres?.some((g) => g.toLowerCase() === tagLower)) return true

        // Check subgenres
        if (book.subgenres?.some((sg) => sg.toLowerCase() === tagLower))
          return true

        // Check relationship type
        if (book.relationshipType?.toLowerCase() === tagLower) return true

        // Check ending type
        if (book.endingType?.toLowerCase() === tagLower) return true

        // Check content warnings
        if (book.contentWarnings?.some((cw) => cw.toLowerCase() === tagLower))
          return true

        // Check spice level
        const spiceMatch = spiceLevels.find(
          (s) => s.name.toLowerCase() === tagLower,
        )
        if (spiceMatch && book.spiceLevel === spiceMatch.level) return true

        // Check darkness level
        const darknessMatch = darknessLevels.find(
          (d) => d.name.toLowerCase() === tagLower,
        )
        if (darknessMatch && book.darknessLevel === darknessMatch.level)
          return true

        // Check POV type
        const povMatch = povTypes.find(
          (p) => p.name.toLowerCase() === tagLower || p.id === tagId,
        )
        if (povMatch && book.pov === povMatch.id) return true

        // Check tense type
        const tenseMatch = tenseTypes.find(
          (t) => t.name.toLowerCase() === tagLower || t.id === tagId,
        )
        if (tenseMatch && book.tense === tenseMatch.id) return true

        // Check character relationships (legacy format support)
        if (
          book.characterRelationships?.some(
            (cr) =>
              cr.type.toLowerCase() === tagLower ||
              cr.style?.some((s) => s.toLowerCase() === tagLower),
          )
        )
          return true

        return false
      })
    })
  }, [selectedTags])

  return {
    selectedTags,
    moodInput,
    setMoodInput,
    addTag,
    removeTag,
    clearAll,
    filteredBooks,
    setSelectedTags,
  }
}
